import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  ErrorHandler,
  LOCALE_ID,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { UrlSerializer } from '@angular/router';
import {
  I18NEXT_SERVICE,
  I18NextModule,
  ITranslationService,
  StrictErrorHandlingStrategy,
} from 'angular-i18next';
import {
  NgcCookieConsentConfig,
} from 'ngx-cookieconsent';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';

import { ApiModule } from '../backend-client/api.module';
import { LayoutModule } from './components/layout/layout.module';
import { MxPagesModule } from './components/pages/mx.pages.module';
import { MxToastModule } from './components/mx.toast/mx.toast.module';
import { MxEnsureModuleLoadedOnceGuard } from './mx.ensure-module-loaded-once.guard';
import { MxGlobalErrorHandler } from './mx.global-error-handler';
import { MxLowerCaseUrlSerializer } from './initializers/mx.lower-case-url-serializer';
import {
  MX_ORDERED_APP_INITIALIZER,
  MX_ORDERED_APP_PROVIDER,
} from './initializers/mx.ordered-initializer';
import { MxHttpHeaderInterceptor } from './interceptors/mx.http-header.interceptor';
import { SzDedupeInterceptor } from './interceptors/sz-dedupe.interceptor';
import { MxJwtInterceptor } from './interceptors/mx.jwt.interceptor';
import { MxServerErrorInterceptor } from './interceptors/mx.server-error.interceptor';
import { MxOverlayModule } from './components/mx.overlay/mx.overlay.module';
import {
  MxAppInitService,
  MxCookieConsentService,
  MxIdentityService,
  MxLoggingService,
} from './services';
import { baseHrefFactory } from './utils';

export function localeIdFactory(i18next: ITranslationService) {
  return i18next.language;
}

export function cookieConsentInitializerFactory(
  service: MxCookieConsentService
) {
  return () => service.init(cookieConfig);
}

export const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: location.hostname,
  },
  palette: {
    popup: {
      background: '#000',
    },
    button: {
      background: '#177597',
    },
  },
  elements: {
    messagelink: `
      <span id="cookieconsent:desc" class="cc-message">{{message}}
        <a aria-label="learn more about cookies" tabindex="0" class="cc-link" href="{{cookiePolicyHref}}" target="_blank">{{link}}</a>
      </span>
      `,
  },
  content: {
    cookiePolicyHref:
      'https://cookie.com',
  },
  theme: 'classic',
  position: 'bottom-left',
  type: 'info',
};

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    I18NextModule.forRoot({
      errorHandlingStrategy: StrictErrorHandlingStrategy,
    }),
    LoggerModule.forRoot({
      level: NgxLoggerLevel.OFF,
    }),
    ApiModule,
    MxOverlayModule,
    LayoutModule,
    MxToastModule,
  ],
  exports: [
    I18NextModule,
    MxOverlayModule,
    LayoutModule,
    MxToastModule,
    MxPagesModule,
  ],
  providers: [
    MX_ORDERED_APP_PROVIDER,
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: cookieConsentInitializerFactory,
      deps: [MxCookieConsentService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxAppInitService) => () => service.versionCheck(),
      deps: [MxAppInitService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxAppInitService) => () => service.loadConfig(),
      deps: [MxAppInitService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxLoggingService) => () => service.load(),
      deps: [MxLoggingService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxAppInitService) => () => service.loadI18Next(),
      deps: [MxAppInitService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxAppInitService) => () => service.loadColorSheme(),
      deps: [MxAppInitService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxAppInitService) => () => service.loadIp(),
      deps: [MxAppInitService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxAppInitService) => () =>
        service.loadSideBarSettings(),
      deps: [MxAppInitService],
      multi: true,
    },
    {
      provide: MX_ORDERED_APP_INITIALIZER,
      useFactory: (service: MxIdentityService) => () =>
        service.refreshTokenLoad(),
      deps: [MxIdentityService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      deps: [I18NEXT_SERVICE],
      useFactory: localeIdFactory,
    },
    { provide: ErrorHandler, useClass: MxGlobalErrorHandler },
    {
      // dupla küldés elleni védelem (azonos, folyamatban lévő módosító kérés nem indul újra)
      provide: HTTP_INTERCEPTORS,
      useClass: SzDedupeInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MxHttpHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MxJwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MxServerErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useFactory: baseHrefFactory,
    },
    {
      provide: UrlSerializer,
      useClass: MxLowerCaseUrlSerializer,
    },
  ], // these should be singleton
})
export class MxCoreModule extends MxEnsureModuleLoadedOnceGuard {
  // Looks for the module in the parent injector to see if it's already been loaded (only want it loaded once)
  constructor(@Optional() @SkipSelf() parentModule: MxCoreModule) {
    super(parentModule, 'AppModule');
  }
}
